import { useApi } from "@ncp/api-client";
import { useSave } from "@ncp/ui";
import type { BaseBody, DefinitionSummary } from "../generic/api";

export type ToolKind = "formula" | "lookup" | "connector";
export const TOOL_KINDS: ToolKind[] = ["formula", "lookup", "connector"];
export type ToolInputType = "number" | "text" | "boolean" | "date";
export const TOOL_INPUT_TYPES: ToolInputType[] = ["number", "text", "boolean", "date"];
export const LOOKUP_OPS = ["eq", "contains", "lt", "lte", "gt", "gte"] as const;

export interface ToolInput { key: string; label?: string | null; type: ToolInputType; required: boolean; description?: string | null }
export interface ToolLookupMatch { field: string; input: string; op?: string | null }
export interface ToolLookup { recordType?: string; match?: ToolLookupMatch[]; returns?: string[]; limit?: number | null; map?: Record<string, string> }
export interface ToolTestCase { name: string; input: Record<string, unknown>; expected: unknown }

export interface ToolBody extends BaseBody {
  kind: ToolKind;
  purpose: string;
  inputs: ToolInput[];
  expression: unknown;
  lookup: ToolLookup | null;
  connectorOperationKey: string | null;
  outputDescription: string | null;
  testCases: ToolTestCase[];
}

export interface ToolSummary extends DefinitionSummary {
  kind?: ToolKind;
  toolKey?: string;
  lastTestPassed?: number | null;
  lastTestTotal?: number | null;
}

export interface ToolTestResult { name: string; passed: boolean; expected: unknown; actual: unknown; error?: string | null }
export interface ToolTestRun { passed: number; total: number; results: ToolTestResult[] }
export interface ToolRunResult { succeeded: boolean; output: unknown; numbers: string[]; error?: string | null }

export function newToolBody(name: string, nameAr: string | null, extra: Record<string, string>): ToolBody {
  const kind = (extra.kind as ToolKind) || "formula";
  return {
    name, nameAr, description: null, descriptionAr: null, tags: [],
    kind,
    purpose: extra.purpose?.trim() ?? "",
    inputs: kind === "formula" ? [{ key: "amount", label: "Amount", type: "number", required: true }] : [],
    expression: kind === "formula" ? { "*": [{ var: "amount" }, 1] } : null,
    lookup: kind === "lookup" ? { recordType: "", match: [], returns: [], limit: 5 } : null,
    connectorOperationKey: null,
    outputDescription: null,
    testCases: [],
  };
}

/** A test input with every declared input set to a placeholder of its type. */
export function inputSkeleton(inputs: ToolInput[]): Record<string, unknown> {
  return Object.fromEntries(inputs.map((i) => [i.key, i.type === "number" ? 0 : i.type === "boolean" ? false : i.type === "date" ? new Date().toISOString().slice(0, 10) : ""]));
}

// Tests and tries store nothing; the designer shows their failure beside the run, with its cause (report: "silent").
export function useToolTest() {
  const api = useApi();
  return useSave({ mutationFn: (input: { body: ToolBody; key?: string }) => api.post<ToolTestRun>("/api/platform/tools/test", input), report: "silent" });
}

/** A try the tool itself failed (`succeeded: false`) is the try's result, not a refusal: it is handed on wrapped so the save helper keeps it. */
export function useToolTry() {
  const api = useApi();
  return useSave({ mutationFn: async (input: { body: ToolBody; input: Record<string, unknown>; key?: string }) => ({ result: await api.post<ToolRunResult>("/api/platform/tools/try", input) }), report: "silent" });
}
